import { Link } from "react-router-dom";
import { ArrowUpRight, HeartHandshake, Hand, ShieldCheck } from "lucide-react";
import { FadeUp, Eyebrow, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { IMAGES } from "@/data/products";

const VALUES = [
  { icon: HeartHandshake, title: "Human Curation", text: "No algorithms, no auto-suggestions. Every brief is read and answered by a person who loves this work." },
  { icon: Hand, title: "Uncompromised Craft", text: "From sourcing to the final fold of tissue, everything passes through our hands — never a warehouse line." },
  { icon: ShieldCheck, title: "Absolute Discretion", text: "What you give, to whom, and why stays between us. Always." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative pt-40 pb-20 lg:pb-28 spotlight overflow-hidden">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
          <FadeUp><Eyebrow>Our Story</Eyebrow></FadeUp>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-chocolate leading-[1.08] tracking-tight mt-6 max-w-4xl">
            <MaskedLine delay={0.1}>A house built on</MaskedLine>
            <MaskedLine delay={0.25} className="italic text-golddark">the art of giving.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-20 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-14 items-start">
          <FadeUp className="lg:col-span-5">
            <div className="relative">
              <div className="aspect-[3/4] overflow-hidden frame-gold">
                <img src={IMAGES.founder} alt="Salem and Sabrina at work in the studio" className="img-warm w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-4 bg-cream border border-gold/40 px-6 py-4 max-w-[240px]">
                <p className="font-serif italic text-lg text-chocolate leading-snug">"Giving should feel as good as receiving."</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-golddark mt-2">Salem &amp; Sabrina</p>
              </div>
            </div>
          </FadeUp>
          <div className="lg:col-span-7 lg:pt-6">
            <FadeUp delay={0.15}>
              <div className="space-y-6 text-base text-chocolate/70 leading-relaxed max-w-2xl">
                <p className="font-serif text-2xl font-light text-chocolate leading-snug">
                  Salem &amp; Sabrina began at a kitchen table, with two friends, a roll of ivory ribbon and a stubborn belief: a gift should say something only you could say.
                </p>
                <p>
                  Between us, we'd spent years being the person everyone called when a gift mattered — the impossible mother-in-law, the client who had everything, the apology that needed to land. We noticed that what people treasured was never the most expensive thing. It was the most <em>considered</em> thing.
                </p>
                <p>
                  So we built the house we wished existed. Part atelier, part concierge desk. A place where you can arrive with nothing but a few honest sentences about someone — and leave with a gift that feels inevitable. Along the way we've sourced first editions, traced discontinued porcelain, wrapped five hundred goodie bags in a weekend, and written more handwritten notes than we can count.
                </p>
                <p>
                  We are deliberately small. Every brief is read by us, every box is wrapped by hand, and every client is known by name. That will never change — it's rather the point.
                </p>
              </div>
              <p className="font-serif italic text-3xl text-golddark mt-10">— Salem &amp; Sabrina</p>
            </FadeUp>
          </div>
        </div>
      </section>

      <Marquee />

      <section className="py-20 lg:py-28 bg-ivory border-b border-chocolate/10">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
          <FadeUp>
            <Eyebrow>What we stand for</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-chocolate mt-5 leading-[1.15]">
              Three promises, <span className="italic text-golddark">kept daily.</span>
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {VALUES.map((v, i) => (
              <FadeUp key={v.title} delay={i * 0.12} className="h-full">
                <div data-testid={`about-value-${i + 1}`} className="h-full bg-cream border border-chocolate/10 p-8 lg:p-10 hover:border-gold/60 transition-colors duration-500">
                  <v.icon size={26} strokeWidth={1.3} className="text-golddark" />
                  <h3 className="font-serif text-2xl font-light text-chocolate mt-6">{v.title}</h3>
                  <p className="text-sm text-chocolate/60 leading-relaxed mt-3">{v.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2} className="mt-14 text-center">
            <Link to="/gift-edit#brief" data-testid="about-cta"
              className="group inline-flex items-center gap-2 bg-chocolate text-cream px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-espresso transition-colors duration-300">
              Create a Gift Edit
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
